// Связки сделок: обращение к своему разделу ядра через BFF.
//
// Устроено как api/xreports.ts и api/nl.ts: свой БАЗА, своя функция запроса, хуки рядом.
// Общего клиента здесь нет намеренно — api/client.ts обслуживает эмуляцию Bitrix REST
// (POST /api/bx), а у модулей ядра форма ответа другая ({rows, total, …}).

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'

// ─────────────────────────── форма ответов ядра ───────────────────────────
// Типы дословно повторяют то, что отдаёт xlinks_api.py. Своих вычислений здесь нет:
// все числа считает ядро запросом, экран только показывает.

/** Вид связки: счётчики. «Не привязано» и «оборвано» — РАЗНЫЕ колонки, и это главное. */
export interface ВидСвязки {
  kind: string
  title: string
  link: string
  entity_type_id: number | null
  rows: number
  linked: number
  unlinked: number
  broken: number
}

export interface Свежесть {
  entity: string
  last_sync_at: string | null
  last_count: number | null
  age_min: number | null
}

export interface СправочникиXL {
  rows: ВидСвязки[]
  total: number
  freshness: Свежесть[]
  coverage_from_default: string
  examples_limit: number
  data_notes: Record<string, string>
}

export interface ПримерОбрыва {
  id: number
  ref: number | null
  title: string | null
  created: string | null
  /** Только у работ 73: true — заявки портала нет в зеркале вовсе. */
  bridge_lost?: boolean
}

export interface Обрыв {
  kind: string
  title: string
  why: string
  count: number
  examples: ПримерОбрыва[]
}

export interface ОбрывыXL {
  rows: Обрыв[]
  total: number
  examples_limit: number
  data_notes: Record<string, string>
}

export interface ПокрытиеСтрока {
  kind: string
  title: string
  deals_linked: number
  /** null — окно пусто, доли не существует (это не ноль). */
  share_pct: number | null
}

export interface ПокрытиеXL {
  rows: ПокрытиеСтрока[]
  total: number
  from: string
  data_notes: Record<string, string>
}

export interface Привязка {
  kind: string
  title: string
  id: number
  name: string | null
  state: string | null
  sum: number | null
  currency: string | null
  created: string | null
  /** false — запись нашлась своим полем-привязкой, а не parentId2. null — портал 73. */
  by_parent: boolean | null
}

export interface СделкаXL {
  bitrix_id: number
  title: string | null
  stage_id: string | null
  opportunity: number | null
  currency: string | null
  closed: boolean | null
  closedate: string | null
  date_create: string | null
}

export interface ПривязкиСделки {
  deal: СделкаXL | null
  rows: Привязка[]
  total: number
  counts: Record<string, number>
  data_notes: Record<string, string>
  /**
   * Ядро ответило 404: сделки нет в зеркале. Это ОТВЕТ, а не ошибка сети, поэтому он
   * едет данными: экран обязан показать объяснение ядра, а не красную плашку.
   */
  not_found?: string
}

const БАЗА = '/api/xlinks'

/** 404 ядра с объяснением: «сделки нет в зеркале». Ответ про данные, а не сбой. */
class НетВЗеркале extends Error {
  constructor(public why: string) {
    super(why)
  }
}

/** Тот же разбор ответа, что в api/xreports.ts и api/nl.ts. */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    if (res.status === 404 && rec.why) throw new НетВЗеркале(String(rec.why))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

// Счётчики связок меняются с шагом синка (минуты), секундная свежесть им не нужна.
const ОБЩЕЕ = { staleTime: 60_000, retry: 1 } as const

export function useXLSprav() {
  return useQuery<СправочникиXL>({
    queryKey: ['xlinks', 'refs'],
    queryFn: () => запрос<СправочникиXL>('/refs'),
    ...ОБЩЕЕ,
  })
}

export function useXLObryvy() {
  return useQuery<ОбрывыXL>({
    queryKey: ['xlinks', 'orphans'],
    queryFn: () => запрос<ОбрывыXL>('/orphans'),
    ...ОБЩЕЕ,
  })
}

export function useXLPokrytie(from?: string) {
  return useQuery<ПокрытиеXL>({
    queryKey: ['xlinks', 'coverage', from ?? ''],
    queryFn: () => запрос<ПокрытиеXL>(
      '/coverage' + (from ? `?from=${encodeURIComponent(from)}` : '')),
    ...ОБЩЕЕ,
  })
}

export function useXLDeal(id: number | null) {
  return useQuery<ПривязкиСделки>({
    queryKey: ['xlinks', 'deal', id],
    queryFn: async () => {
      try {
        return await запрос<ПривязкиСделки>(`/deal?id=${id}`)
      } catch (e) {
        // 404 ядра — «сделки нет в зеркале». Это содержательный ответ про данные,
        // и он не должен рисоваться той же плашкой, что обрыв сети. Объяснение
        // приходит от ядра (поле why) и едет на экран дословно.
        if (e instanceof НетВЗеркале) {
          return { deal: null, rows: [], total: 0, counts: {}, data_notes: {},
                   not_found: e.why }
        }
        throw e
      }
    },
    enabled: id !== null && id > 0,
    ...ОБЩЕЕ,
  })
}
